import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { useSession } from '../session'
import { resolveReturnTo } from '../auth/redirect'
import { AuthLayout } from '../components/AuthLayout'
import { useT } from '../i18n'

const COUNTDOWN_SECONDS = 5

// Logout authority (ADR 0053). The Accounts App owns the cookie/session lifecycle, so logout happens
// here: clear the Audience Bridge Cookie + this app's own tokens, confirm "signed out", then send the
// user back to the caller (store, grow, …) after a short countdown — or immediately via "Go back".
// There is intentionally no "sign in again" here; re-authentication is owned by each app's own flow.
export function LogoutPage() {
  const { logout } = useSession()
  const t = useT()
  const [searchParams] = useSearchParams()
  const returnTo = resolveReturnTo(searchParams, '')
  const canReturn = returnTo.startsWith('http')
  // Inside a native shell (grow-mobile web-view) the countdown is just friction — return immediately.
  const immediate = Capacitor.isNativePlatform()
  const [count, setCount] = useState(COUNTDOWN_SECONDS)

  // Clear tokens + cookie on mount. This effect runs before the SessionProvider bootstrap effect
  // (children run before parents), so a stale refresh token can't silently re-establish the session.
  useEffect(() => {
    logout()
  }, [logout])

  // Countdown, then auto-return to the caller. Only when we have an allow-listed external caller —
  // a direct visit to /logout (no redirect_uri) just shows the confirmation.
  useEffect(() => {
    if (!canReturn) return
    if (immediate || count <= 0) {
      window.location.href = returnTo
      return
    }
    const id = setTimeout(() => setCount((c) => c - 1), 1000)
    return () => clearTimeout(id)
  }, [count, canReturn, returnTo, immediate])

  return (
    <AuthLayout title={t.signedOutTitle} subtitle={t.signedOutBody}>
      {canReturn && (
        <div className="space-y-3 text-center">
          <p className="text-sm text-base-content/60">{t.goingBackIn.replace('{{n}}', String(count))}</p>
          <button className="btn btn-primary w-full" onClick={() => { window.location.href = returnTo }}>
            {t.returnToApp}
          </button>
        </div>
      )}
    </AuthLayout>
  )
}
